import { type ReactNode, useMemo, useRef } from 'react';
import { Platform, type View } from 'react-native';

import { CheckboxContext } from './context';
import type { CheckedState } from './types';

export type CheckboxProps = {
  children: ReactNode;
  onChange: (checked: CheckedState) => void;
  checked: CheckedState;
  customLabelComponent?: React.ReactNode;
  unavailable?: boolean;
  disabled?: boolean;
  softDisabled?: boolean;
  onSoftDisabledPress?: () => void;
  label: string;
};

export function Checkbox({
  children,
  onChange,
  checked,
  unavailable = false,
  disabled = false,
  softDisabled = false,
  onSoftDisabledPress,
  label,
  customLabelComponent,
}: CheckboxProps) {
  const pressableRef = useRef<View | null>(null);
  const contextValue = useMemo(
    () => ({
      label,
      customLabelComponent,
      handlePress: () => {
        if (unavailable) {
          return;
        }

        if (softDisabled) {
          onSoftDisabledPress?.();
          return;
        }

        if (disabled) {
          return;
        }

        if (checked === 'mixed') {
          onChange(true);
        } else {
          onChange(!checked);
        }

        if (Platform.OS === 'web') {
          pressableRef.current?.focus?.();
        }
      },
      checked,
      unavailable,
      disabled,
      softDisabled,
      onSoftDisabledPress,
      pressableRef,
    }),
    [
      label,
      customLabelComponent,
      checked,
      unavailable,
      disabled,
      softDisabled,
      onSoftDisabledPress,
      onChange,
    ]
  );

  return <CheckboxContext.Provider value={contextValue}>{children}</CheckboxContext.Provider>;
}
